// src/components/Generic/VideoPlayer.js
import Hls from "hls.js";
import Plyr from "plyr-react";
import "plyr-react/plyr.css";
import React, { useEffect, useRef, useState } from "react";

const VIDEO_FILE_EXTENSIONS = [".mp4", ".webm", ".m4v", ".mov", ".ogv"];
const HLS_EXTENSION = ".m3u8";

function getUrlPathname(url) {
  try {
    return new URL(url).pathname.toLowerCase();
  } catch (error) {
    return String(url || "").split(/[?#]/)[0].toLowerCase();
  }
}

function getSafeMediaLogUrl(url) {
  try {
    const parsedUrl = new URL(url);
    return `${parsedUrl.origin}${parsedUrl.pathname}`;
  } catch (error) {
    return "invalid_url";
  }
}

export function getYoutubeVideoId(url) {
  try {
    const parsedUrl = new URL(url);
    const hostname = parsedUrl.hostname.replace(/^www\./, "");
    const pathParts = parsedUrl.pathname.split("/").filter(Boolean);

    if (hostname === "youtu.be") return pathParts[0] || "";
    if (!["youtube.com", "m.youtube.com", "youtube-nocookie.com"].includes(hostname)) return "";

    if (parsedUrl.pathname === "/watch") return parsedUrl.searchParams.get("v") || "";
    if (["embed", "shorts", "live"].includes(pathParts[0])) return pathParts[1] || "";
  } catch (error) {
    return "";
  }

  return "";
}

// VIMEO URL CONTRACT
// Vimeo links come in three embeddable shapes, each with a different embed URL:
//   video    — vimeo.com/{id}, vimeo.com/{id}/{hash} (unlisted), player.vimeo.com/video/{id}?h={hash},
//              channels/{name}/{id}, groups/{name}/videos/{id}, showcase|album/{sid}/video/{id},
//              manage/videos/{id}/{hash}  →  player.vimeo.com/video/{id}[?h={hash}] (Plyr Vimeo provider)
//   event    — vimeo.com/event/{eventId}[/embed][/{hash}]  →  vimeo.com/event/{eventId}/embed[/{hash}] (iframe)
//   showcase — vimeo.com/showcase|album/{sid}[/embed]  →  vimeo.com/showcase/{sid}/embed (iframe)
// Event IDs and unlisted hashes are NOT video IDs — feeding them to player.vimeo.com/video/ yields
// Vimeo's "Sorry, this video does not exist" page (BIOTECH live-event regression).
const VIMEO_HASH = /^[0-9a-f]{6,}$/i;
const NUMERIC = /^\d+$/;

export function parseVimeoUrl(url) {
  try {
    const parsedUrl = new URL(url);
    const hostname = parsedUrl.hostname.replace(/^www\./, "");
    const parts = parsedUrl.pathname.split("/").filter(Boolean);
    const queryHash = parsedUrl.searchParams.get("h") || "";

    if (hostname === "player.vimeo.com") {
      if (parts[0] === "video" && NUMERIC.test(parts[1] || "")) {
        const pathHash = VIMEO_HASH.test(parts[2] || "") ? parts[2] : "";
        return { kind: "video", id: parts[1], hash: queryHash || pathHash };
      }
      return null;
    }
    if (hostname !== "vimeo.com") return null;

    if (parts[0] === "event" && NUMERIC.test(parts[1] || "")) {
      const hash = parts.slice(2).find((part) => part !== "embed" && VIMEO_HASH.test(part)) || "";
      return { kind: "event", id: parts[1], hash };
    }

    if (["showcase", "album"].includes(parts[0]) && NUMERIC.test(parts[1] || "")) {
      if (parts[2] === "video" && NUMERIC.test(parts[3] || "")) {
        return { kind: "video", id: parts[3], hash: queryHash };
      }
      return { kind: "showcase", id: parts[1], hash: "" };
    }

    const idIndex = parts.findIndex((part) => NUMERIC.test(part));
    if (idIndex === -1) return null;
    const pathHash = VIMEO_HASH.test(parts[idIndex + 1] || "") ? parts[idIndex + 1] : "";
    return { kind: "video", id: parts[idIndex], hash: queryHash || pathHash };
  } catch (error) {
    return null;
  }
}

export function getVimeoVideoId(url) {
  const vimeo = parseVimeoUrl(url);
  return vimeo?.kind === "video" ? vimeo.id : "";
}

function getVimeoPlayerUrl({ id, hash }) {
  return `https://player.vimeo.com/video/${id}${hash ? `?h=${hash}` : ""}`;
}

export function getEmbeddableVideoUrl(url) {
  const youtubeId = getYoutubeVideoId(url);
  if (youtubeId) return `https://www.youtube.com/embed/${youtubeId}`;

  const vimeo = parseVimeoUrl(url);
  if (vimeo?.kind === "video") return getVimeoPlayerUrl(vimeo);
  if (vimeo?.kind === "event") {
    return `https://vimeo.com/event/${vimeo.id}/embed${vimeo.hash ? `/${vimeo.hash}` : ""}`;
  }
  if (vimeo?.kind === "showcase") return `https://vimeo.com/showcase/${vimeo.id}/embed`;

  return url;
}

export const isHlsUrl = (url) => getUrlPathname(url).endsWith(HLS_EXTENSION);
export const isVideoFile = (url) =>
  VIDEO_FILE_EXTENSIONS.some((ext) => getUrlPathname(url).endsWith(ext)) || isHlsUrl(url);

function getFileMimeType(url) {
  const pathname = getUrlPathname(url);
  if (pathname.endsWith(".mp4") || pathname.endsWith(".m4v")) return "video/mp4";
  if (pathname.endsWith(".webm")) return "video/webm";
  if (pathname.endsWith(".ogv")) return "video/ogg";
  if (pathname.endsWith(".mov")) return "video/quicktime";
  return undefined;
}

// How playback (play/pause) is observed for analytics. Cross-origin iframes cannot be inspected (no network or DOM
// access from our page), so: "player" = our Plyr/HLS player events; "vimeo_api" = Vimeo embed postMessage API
// (works for live events + showcases); "interaction" = generic iframe, play inferred from the first click into it.
export function getPlaybackSignal(url) {
  if (getVideoSourceKind(url) !== "embed") return "player";
  return parseVimeoUrl(url) ? "vimeo_api" : "interaction";
}

function getVideoSourceKind(url) {
  if (isHlsUrl(url)) return "hls";
  if (isVideoFile(url)) return "file";
  if (getYoutubeVideoId(url)) return "youtube";
  if (getVimeoVideoId(url)) return "vimeo";
  return "embed";
}

function getPlyrSource(url) {
  const youtubeId = getYoutubeVideoId(url);
  if (youtubeId) {
    return { type: "video", sources: [{ src: youtubeId, provider: "youtube" }] };
  }

  // Plyr's Vimeo provider reads the unlisted hash from `?h=`; a bare id is kept for public videos.
  const vimeo = parseVimeoUrl(url);
  if (vimeo?.kind === "video") {
    const src = vimeo.hash ? getVimeoPlayerUrl(vimeo) : vimeo.id;
    return { type: "video", sources: [{ src, provider: "vimeo" }] };
  }

  return {
    type: "video",
    sources: [{ src: url, type: getFileMimeType(url) }],
  };
}

function getPlaybackHandlers(onPlaybackStateChange) {
  return {
    onPlay: () => onPlaybackStateChange?.(true),
    onPlaying: () => onPlaybackStateChange?.(true),
    onPause: () => onPlaybackStateChange?.(false),
    onEnded: () => onPlaybackStateChange?.(false),
    onError: () => onPlaybackStateChange?.(false),
  };
}

function safeDetachPlyrEvent(plyr, eventName, handler, sourceKey) {
  try {
    plyr.off(eventName, handler);
  } catch (error) {
    console.debug(
      "PLYR_EVENT_BRIDGE_DETACH_SKIPPED",
      `media_url=${getSafeMediaLogUrl(sourceKey)} event=${eventName} code=${error?.name || "unknown"}`
    );
  }
}

function usePlyrPlaybackBridge(plyrRef, sourceKey, onPlaybackStateChange) {
  useEffect(() => {
    let cleanup = () => {};
    let retryTimer = null;
    let isCancelled = false;
    let attempts = 0;
    const handlePlaying = () => onPlaybackStateChange?.(true);
    const handleStopped = () => onPlaybackStateChange?.(false);

    const bindWhenReady = () => {
      if (isCancelled) return;

      const plyr = plyrRef.current?.plyr;
      if (plyr && typeof plyr.on === "function" && typeof plyr.off === "function") {
        plyr.on("play", handlePlaying);
        plyr.on("playing", handlePlaying);
        plyr.on("pause", handleStopped);
        plyr.on("ended", handleStopped);
        plyr.on("error", handleStopped);
        cleanup = () => {
          safeDetachPlyrEvent(plyr, "play", handlePlaying, sourceKey);
          safeDetachPlyrEvent(plyr, "playing", handlePlaying, sourceKey);
          safeDetachPlyrEvent(plyr, "pause", handleStopped, sourceKey);
          safeDetachPlyrEvent(plyr, "ended", handleStopped, sourceKey);
          safeDetachPlyrEvent(plyr, "error", handleStopped, sourceKey);
        };
        console.debug("PLYR_EVENT_BRIDGE_ATTACHED", `media_url=${getSafeMediaLogUrl(sourceKey)}`);
        return;
      }

      attempts += 1;
      if (attempts < 20) retryTimer = window.setTimeout(bindWhenReady, 50);
    };

    bindWhenReady();

    return () => {
      isCancelled = true;
      if (retryTimer) window.clearTimeout(retryTimer);
      cleanup();
      onPlaybackStateChange?.(false);
    };
  }, [onPlaybackStateChange, plyrRef, sourceKey]);
}

function HlsVideo({ src, onPlaybackStateChange }) {
  const videoRef = useRef(null);
  const [errorMessage, setErrorMessage] = useState("");
  const playbackHandlers = getPlaybackHandlers(onPlaybackStateChange);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return undefined;

    let didRetryNetworkError = false;
    setErrorMessage("");

    const clearVideoSource = () => {
      video.removeAttribute("src");
      video.load();
    };

    if (Hls.isSupported()) {
      const hls = new Hls({ enableWorker: true, lowLatencyMode: true });
      const hlsErrorEvent = Hls.Events?.ERROR || "hlsError";

      const handleHlsError = (_event, data = {}) => {
        console.warn(
          "HLS_PLAYBACK_ERROR",
          `media_url=${getSafeMediaLogUrl(src)} fatal=${Boolean(data.fatal)} type=${data.type || "unknown"} details=${data.details || "unknown"}`
        );

        if (!data.fatal) return;

        if (data.type === Hls.ErrorTypes?.MEDIA_ERROR) {
          hls.recoverMediaError();
          return;
        }

        if (data.type === Hls.ErrorTypes?.NETWORK_ERROR && !didRetryNetworkError) {
          didRetryNetworkError = true;
          hls.startLoad();
          return;
        }

        setErrorMessage("This HLS stream could not be loaded. Check that the .m3u8 URL is public and allows browser playback/CORS.");
      };

      hls.on(hlsErrorEvent, handleHlsError);
      hls.loadSource(src);
      hls.attachMedia(video);
      console.info("HLS_SOURCE_ATTACHED", `media_url=${getSafeMediaLogUrl(src)}`);

      return () => {
        hls.off?.(hlsErrorEvent, handleHlsError);
        hls.destroy();
        clearVideoSource();
        onPlaybackStateChange?.(false);
      };
    }

    if (video.canPlayType("application/vnd.apple.mpegurl")) {
      const handleNativeError = () => {
        console.warn("HLS_NATIVE_PLAYBACK_ERROR", `media_url=${getSafeMediaLogUrl(src)}`);
        setErrorMessage("This HLS stream could not be loaded. Check that the .m3u8 URL is public and allows browser playback/CORS.");
      };

      video.addEventListener("error", handleNativeError);
      video.src = src;
      video.load();
      console.info("HLS_NATIVE_SOURCE_ATTACHED", `media_url=${getSafeMediaLogUrl(src)}`);

      return () => {
        video.removeEventListener("error", handleNativeError);
        clearVideoSource();
        onPlaybackStateChange?.(false);
      };
    }

    setErrorMessage("This browser cannot play HLS streams.");
    return () => onPlaybackStateChange?.(false);
  }, [src, onPlaybackStateChange]);

  return (
    <>
      <video ref={videoRef} controls playsInline crossOrigin="anonymous" {...playbackHandlers} />
      {errorMessage && (
        <div className="absolute inset-x-4 bottom-4 rounded-lg border border-amber-300 bg-amber-50 px-4 py-3 text-sm font-semibold text-amber-900 shadow-lg">
          {errorMessage}
        </div>
      )}
    </>
  );
}

const VIMEO_SUBSCRIBE_EVENTS = ["play", "playing", "pause", "ended", "error"];
const VIMEO_PLAYING_EVENTS = new Set(["play", "playing"]);
const VIMEO_STOPPED_EVENTS = new Set(["pause", "ended", "error"]);

function parseMessageData(data) {
  if (typeof data !== "string") return data;
  try {
    return JSON.parse(data);
  } catch (error) {
    return null;
  }
}

// Vimeo player postMessage protocol (vimeo/player.js): host posts {method:"addEventListener", value:event};
// the iframe answers {event:"play"|"pause"|...}. Verified live against vimeo.com/event/{id}/embed iframes.
function useVimeoEmbedBridge(iframeRef, src, enabled, onPlaybackStateChange) {
  useEffect(() => {
    const iframe = iframeRef.current;
    if (!enabled || !iframe || !onPlaybackStateChange) return undefined;
    let targetOrigin;
    try {
      targetOrigin = new URL(src).origin;
    } catch (error) {
      return undefined;
    }

    const subscribe = () => {
      const target = iframe.contentWindow;
      if (!target) return;
      VIMEO_SUBSCRIBE_EVENTS.forEach((value) => target.postMessage({ method: "addEventListener", value }, targetOrigin));
    };
    const handleMessage = (event) => {
      if (event.source !== iframe.contentWindow) return;
      let host = "";
      try {
        host = new URL(event.origin).hostname;
      } catch (error) {
        return;
      }
      if (host !== "vimeo.com" && !host.endsWith(".vimeo.com")) return;
      const data = parseMessageData(event.data);
      if (data?.event === "ready") return subscribe();
      if (VIMEO_PLAYING_EVENTS.has(data?.event)) onPlaybackStateChange(true);
      else if (VIMEO_STOPPED_EVENTS.has(data?.event)) onPlaybackStateChange(false);
      else return;
      console.debug("VIMEO_EMBED_PLAYBACK_EVENT", `media_url=${getSafeMediaLogUrl(src)} event=${data.event}`);
    };

    window.addEventListener("message", handleMessage);
    iframe.addEventListener("load", subscribe);
    subscribe();
    return () => {
      window.removeEventListener("message", handleMessage);
      iframe.removeEventListener("load", subscribe);
      onPlaybackStateChange(false);
    };
  }, [iframeRef, src, enabled, onPlaybackStateChange]);
}

// Generic iframes expose nothing. Best available signal: the window loses focus to the embed (= the viewer
// clicked into the player). Reported as playing from then on; dashboards label this signal as estimated.
function useIframeInteractionBridge(iframeRef, src, enabled, onPlaybackStateChange) {
  useEffect(() => {
    const iframe = iframeRef.current;
    if (!enabled || !iframe || !onPlaybackStateChange) return undefined;
    let timer = null;
    const handleBlur = () => {
      timer = window.setTimeout(() => {
        if (document.activeElement !== iframe) return;
        console.info("EMBED_INTERACTION_DETECTED", `media_url=${getSafeMediaLogUrl(src)}`);
        onPlaybackStateChange(true);
      }, 0);
    };
    window.addEventListener("blur", handleBlur);
    return () => {
      window.removeEventListener("blur", handleBlur);
      if (timer) window.clearTimeout(timer);
      onPlaybackStateChange(false);
    };
  }, [iframeRef, src, enabled, onPlaybackStateChange]);
}

function EmbedFrame({ src, signal, onPlaybackStateChange }) {
  const iframeRef = useRef(null);
  useVimeoEmbedBridge(iframeRef, src, signal === "vimeo_api", onPlaybackStateChange);
  useIframeInteractionBridge(iframeRef, src, signal === "interaction", onPlaybackStateChange);

  return (
    <iframe
      ref={iframeRef}
      title="video player"
      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
      referrerPolicy="strict-origin-when-cross-origin"
      allowFullScreen
      src={src}
    />
  );
}

function PlyrVideo({ source, sourceKey, onPlaybackStateChange }) {
  const plyrRef = useRef(null);
  usePlyrPlaybackBridge(plyrRef, sourceKey, onPlaybackStateChange);

  return (
    <Plyr
      ref={plyrRef}
      source={source}
      options={{
        controls: ["play", "progress", "current-time", "mute", "volume", "fullscreen"],
        ratio: "16:9",
      }}
      {...getPlaybackHandlers(onPlaybackStateChange)}
    />
  );
}

export default function VideoPlayer({ video_url, onPlaybackStateChange }) {
  const sourceKind = getVideoSourceKind(video_url);
  const embeddableVideoUrl = getEmbeddableVideoUrl(video_url);

  return (
    <div id="video-player">
      {sourceKind === "hls" ? (
        <HlsVideo src={video_url} onPlaybackStateChange={onPlaybackStateChange} />
      ) : sourceKind === "embed" ? (
        <EmbedFrame
          src={embeddableVideoUrl}
          signal={getPlaybackSignal(video_url)}
          onPlaybackStateChange={onPlaybackStateChange}
        />
      ) : (
        <PlyrVideo
          source={getPlyrSource(video_url)}
          sourceKey={video_url}
          onPlaybackStateChange={onPlaybackStateChange}
        />
      )}
    </div>
  );
}
